// Coming back from checkout.
//
// The whole job of this page is refusing to guess. `?status=success` in the URL
// means the payment page thought it worked — it is not proof, and it is
// trivially forgeable. So the status is polled until the server says `complete`,
// and only then is the membership re-read and the visitor let through.
//
// The webhook that grants entitlement lags the payment by seconds in the real
// world (the mock simulates two polls of it), which is why this screen exists at
// all rather than redirecting straight back to review.

import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import styled from "styled-components";
import i18next from "i18next";
import { iRootState } from "../../../store";
import SubmissionLayout from "../common/SubmissionLayout";
import {
  clearStoredCheckout,
  readStoredCheckout,
} from "../../../models/submissionStep4Model";
import { isSubmissionAuthed } from "../../../helpers/submissionAuth";
import { setSubmissionAnalyticsContext, trackCheckoutCompleted, trackSubmissionEvent } from "../analytics";
import { PAID_PARAM } from "../common/membershipPaywall";
import { withParam } from "../common/submissionParams";
import { membershipPrimary } from "../common/membershipButton";

interface Props extends StateProps, DispatchProps {
  history: any;
  location: any;
}

// Polling, backing off, and a ceiling. Past the ceiling the trial is almost
// certainly active and the webhook is merely slow, so the visitor is not left
// staring at a spinner — see below.
const POLL_DELAYS_MS = [400, 800, 1200, 2000, 2600, 3200, 4000, 5000];
const REVIEW_PATH = "/submission/flow/step/4";
const PAYWALL_PATH = "/submission/paywall";

const CheckoutReturnPage: React.FC<Props> = ({
  history,
  location,
  checkoutSessionId,
  pollCheckoutStatus,
  loadMembership,
}) => {
  const [message, setMessage] = useState(i18next.t("Confirming your trial…"));
  const [slow, setSlow] = useState(false);
  const timerRef = useRef<any>(null);
  const cancelledRef = useRef(false);

  const params = new URLSearchParams(location.search);
  const urlStatus = params.get("status");
  const stored = readStoredCheckout();
  const sessionId =
    params.get("session_id") || checkoutSessionId || (stored && stored.checkoutSessionId);
  const returnPath = (stored && stored.returnPath) || REVIEW_PATH;
  // The flow's query string (bd, roleid, step4, ctry), carried in the stored
  // return path. The paywall exits below need it too: without it a declined
  // card landed on a paywall that no longer knew which breakdown it was for.
  const flowSearch =
    returnPath.indexOf("?") >= 0 ? returnPath.slice(returnPath.indexOf("?")) : "";
  const paywallPath = `${PAYWALL_PATH}${flowSearch}`;
  // Back to step 4 marked paid, as the membership site's completion pages do
  // (CheckoutReturnGate): step 4 then waits for the plan and, in test A, sends.
  const returnPathname =
    returnPath.indexOf("?") >= 0 ? returnPath.slice(0, returnPath.indexOf("?")) : returnPath;
  const paidReturnPath = `${returnPathname}${withParam(flowSearch, PAID_PARAM, "1")}`;

  useEffect(() => {
    if (!isSubmissionAuthed()) {
      history.replace("/submission/flow/verify-email");
      return;
    }
    if (!sessionId) {
      // Nothing to confirm. Back to the offer rather than inventing an outcome.
      history.replace(paywallPath);
      return;
    }

    // An explicit cancel needs no polling: the visitor told us. The paywall's
    // failure modal explains it from there.
    if (urlStatus === "cancel") {
      trackSubmissionEvent("sf_step4_checkout_cancelled", { checkoutSessionId: sessionId });
      clearStoredCheckout();
      history.replace(paywallPath);
      return;
    }

    let attempt = 0;
    const poll = async () => {
      if (cancelledRef.current) return;
      const status = await pollCheckoutStatus(sessionId);

      // A rejection (declined, expired, unknown session) already stored the
      // reason on the model; the paywall shows it.
      if (!status) {
        trackSubmissionEvent("sf_step4_checkout_failed", { checkoutSessionId: sessionId });
        clearStoredCheckout();
        history.replace(paywallPath);
        return;
      }

      if (status.status === "complete") {
        // Re-read the membership: payment is the one thing that changes the
        // answer, and every screen after this routes on it.
        await loadMembership();
        // The server says the checkout is complete: the plan is theirs, even while the membership
        // still lags behind Stripe's webhook, so the event says "pro" as step 4's does. Sent here,
        // not left to step 4: a webhook slower than step 4's wait would lose it.
        setSubmissionAnalyticsContext({ onPlan: true });
        // Once per casting call: step 4 says so too, back with paid=1 (tests A and B).
        trackCheckoutCompleted(new URLSearchParams(flowSearch).get("bd"), {
          checkoutSessionId: sessionId,
        });
        clearStoredCheckout();
        history.replace(paidReturnPath);
        return;
      }

      attempt += 1;
      if (attempt >= POLL_DELAYS_MS.length) {
        // Out of patience, not out of luck: the payment went through and the
        // webhook is still catching up. Soft-landing beats a hard failure that
        // would tell someone their trial did not start when it did.
        setSlow(true);
        setMessage(i18next.t("Payment received — your trial is activating."));
        return;
      }
      timerRef.current = setTimeout(poll, POLL_DELAYS_MS[attempt]);
    };

    timerRef.current = setTimeout(poll, POLL_DELAYS_MS[0]);
    return () => {
      cancelledRef.current = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <SubmissionLayout>
      <Centered aria-busy={!slow}>
        <Spinner aria-hidden="true" />
        <Message>{message}</Message>
        {slow && (
          <>
            <SubMessage>
              {i18next.t("You can carry on — we'll finish this in the background.")}
            </SubMessage>
            <PrimaryButton onClick={() => history.replace(paidReturnPath)}>
              {i18next.t("Continue")}
            </PrimaryButton>
          </>
        )}
      </Centered>
    </SubmissionLayout>
  );
};

const Centered = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 48px 0;
`;

const Spinner = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 3px solid #e3e8ee;
  border-top-color: #0f9bf0;
  margin-bottom: 14px;
  animation: sfSpin 0.9s linear infinite;

  @keyframes sfSpin {
    to {
      transform: rotate(360deg);
    }
  }

  /* Someone who has asked for less motion still needs to see that something is
     happening, so the ring stays and only the spinning stops. */
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Message = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #122640;
`;

const SubMessage = styled.div`
  font-size: 13px;
  color: #53627c;
  margin-top: 8px;
  line-height: 20px;
`;

const PrimaryButton = styled.button`
  width: 100%;
  margin-top: 18px;
  ${membershipPrimary}
  padding: 14px 0;
  min-height: 48px;
`;

interface StateProps {
  checkoutSessionId: string | null;
}

interface DispatchProps {
  pollCheckoutStatus: (checkoutSessionId: string) => Promise<any>;
  loadMembership: () => Promise<any>;
}

const mapStateToProps = (state: iRootState): StateProps => ({
  checkoutSessionId: state.submissionStep4Model.checkoutSessionId,
});

const mapDispatchToProps = (dispatch: any): DispatchProps => ({
  pollCheckoutStatus: dispatch.submissionStep4Model.pollCheckoutStatus,
  loadMembership: dispatch.submissionStep4Model.loadMembership,
});

export default connect(mapStateToProps, mapDispatchToProps)(CheckoutReturnPage);
